import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import DocumentsModern from 'src/components/Documents/DocumentsModern';
import LoadState from 'src/components/LoadState/LoadState';
import ConfirmationModal from 'src/components/ConfirmationModal/ConfirmationModal';
import { ContractFilesRepeater } from 'src/components/ContractFilesRepeater/ContractFilesRepater';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { useBranding } from 'src/branding/BrandingContextProvider';
import {
  useSuiteDocumentsAPI,
  UseSuiteDocumentsAPIReturn,
} from 'src/pages/Suite/Hooks/useSuiteDocumentsAPI';
import { DocumentFormData } from 'src/utils/S3UploadDocumentsUtility';
import { IContractFile } from '../../../public/APIContracts/APIContracts';

interface Suite {
  id: string;
  number: string;
  isSpark: boolean;
}

interface SuiteDocumentsProps {
  suite: Suite;
  properties: any;
  contractFiles?: IContractFile[];
}

const SuiteDocuments = ({ suite, contractFiles }: SuiteDocumentsProps) => {
  const user: any = useSelector(selectAuthenticatedUser);

  const { branding } = useBranding();

  const roleName: string = user?.roles?.[0]?.role?.name ?? '';

  const [documentToDelete, setDocumentToDelete] = useState<any | null>(null);

  const suiteDocumentsAPIOptions = {
    suiteId: suite.id,
    userId: user.id,
    roleName,
    customUploadErrorMessage: `Failed to upload document to ${branding.text.suite}. Please try again or contact Juniper support if the issue persists.`,
  };

  const {
    documents,
    loadingDocs,
    isSaving,
    fetchDocuments,
    saveDocument,
    deleteDocument,
  }: UseSuiteDocumentsAPIReturn = useSuiteDocumentsAPI(
    suiteDocumentsAPIOptions
  );

  useEffect(() => {
    void fetchDocuments(suite.id);
  }, [suite.id]);

  const onSave = async (document: DocumentFormData): Promise<void> => {
    await saveDocument(document, suite.id, user.id);
  };

  const onDeleteRequest = (document: any): void => {
    setDocumentToDelete(document);
  };

  const confirmDelete = async (): Promise<void> => {
    if (!documentToDelete) {
      return;
    }

    await deleteDocument(documentToDelete);
    setDocumentToDelete(null);
  };

  const cancelDelete = (): void => setDocumentToDelete(null);

  return (
    <>
      {documentToDelete && (
        <ConfirmationModal
          title="Delete Document"
          message={
            <>
              Are you sure you want to delete{' '}
              <strong>{documentToDelete.name}</strong>?
            </>
          }
          warningMessage={
            <>
              <strong>Warning:</strong> deleting this document will delete this
              from the catalog and all other units and associated projects.
            </>
          }
          confirmLabel="Delete"
          cancelLabel="Go Back"
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
          variant="danger"
        />
      )}
      <DocumentsModern
        documents={documents}
        user={user}
        labelName="Manuals & Documents"
        saving={isSaving}
        loading={loadingDocs}
        onSave={onSave}
        onDeleteDocument={onDeleteRequest}
        groupDocuments
        from="Suite"
        introContent
        useCustomConfirmation
      />
      {contractFiles && <ContractFilesRepeater contractFiles={contractFiles} />}
    </>
  );
};

export default SuiteDocuments;
